import {IReview} from "../models/IReview.ts";
import {createContext, ReactNode, useContext, useState} from "react";
import AuthContext from "./AuthContext.tsx";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios from "axios";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api'

interface IReviewContextProps {
    reviews?: IReview[]
    review?: IReview
    setSelectedReviewId: (id: string) => void
    createReviewMutation: any
    errorCreatingReview: boolean
    updateReviewMutation: any
    errorUpdatingReview: boolean
    deleteImageMutation: any
    deleteImageError: any
    addImageMutation: any
    addImageError: any
}

const ReviewContext = createContext<IReviewContextProps>({} as IReviewContextProps);

interface IReviewProviderProvider {
    children: ReactNode
}

export const ReviewProvider = ({children}: IReviewProviderProvider) => {
    const queryClient = useQueryClient();
    const {isLoggedIn} = useContext(AuthContext)
    const [selectedReviewId, setSelectedReviewId] = useState<string>()
    const [errorCreatingReview, setErrorCreatingReview] = useState<boolean>(false)
    const [errorUpdatingReview, setErrorUpdatingReview] = useState<boolean>(false)
    const [deleteImageError, setDeleteImageError] = useState<any>()
    const [addImageError, setAddImageError] = useState<any>()

    const {data: reviews} = useQuery<IReview[]>({
        queryKey: ["reviews"],
        retry: false,
        queryFn: async () => {
            const response = await axios.get(`${VITE_API_BASE_URL}/review`, {withCredentials: true})
            return response.data
        },
    })

    const {data: review} = useQuery<IReview>({
        queryKey: ["review", selectedReviewId],
        enabled: !!selectedReviewId,
        retry: false,
        queryFn: async () => {
            const response = await axios.get(`${VITE_API_BASE_URL}/review/${selectedReviewId}`, {withCredentials: true})
            return response.data
        }
    })

    const createReviewMutation = useMutation({
        mutationFn: async (review: IReview) => {
            const response = isLoggedIn
                ? await axios.post(`${VITE_API_BASE_URL}/review`, {review}, {withCredentials: true})
                : await axios.post(`${VITE_API_BASE_URL}/review/session`, {review}, {withCredentials: true})
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["reviews"]})
            queryClient.invalidateQueries({queryKey: ["orders"]})
        },
        onError: () => {
            setErrorCreatingReview(true)
        }
    })

    const updateReviewMutation = useMutation({
        mutationFn: async (review: IReview) => {
            const response = isLoggedIn
                ? await axios.put(`${VITE_API_BASE_URL}/review/${review._id}`, {review}, {withCredentials: true})
                : await axios.put(`${VITE_API_BASE_URL}/review/session/${review._id}`, {review}, {withCredentials: true})
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["reviews"]})
            queryClient.invalidateQueries({queryKey: ["orders"]})
        },
        onError: () => {
            setErrorUpdatingReview(true)
        }
    })

    const deleteImageMutation = useMutation({
        mutationFn: async (cleanedUrl: string) =>
            await axios.delete(`${VITE_API_BASE_URL}/delete/${cleanedUrl}`, {withCredentials: true}),
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: ['review']}),
        onError: error => setDeleteImageError(error)
    })

    const addImageMutation = useMutation({
        mutationFn: async (imageData) => {
            const response = await axios.post(`${VITE_API_BASE_URL}/upload`, imageData, {
                withCredentials: true,
                headers: {'Content-Type': 'multipart/form-data'},
            })
            return response.data
        },
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: ['review']}),
        onError: error => setAddImageError(error)
    })

    return (
        <ReviewContext.Provider
            value={{
                reviews,
                review,
                setSelectedReviewId,
                createReviewMutation,
                errorCreatingReview,
                updateReviewMutation,
                errorUpdatingReview,
                deleteImageMutation,
                deleteImageError,
                addImageMutation,
                addImageError
            }}>
            {children}
        </ReviewContext.Provider>
    )
}

export default ReviewContext;